import * as gql from "gql-query-builder";

import {
  AccountsBalancesPayload,
  UserPayload,
  EstimatePaymentFeePayload,
  PaymentsLimitsPayload,
  CalculatePaymentsLimitsPayload,
  CreateWithdrawalFiatPayload,
  PaymentsRoutesPayload,
  CreateConversionQuotePayload,
} from "./types";

import {
  accountsBalancesFields,
  userFields,
  paymentsLimitsFields,
  calculatedPaymentsLimitsFields,
  createWithdrawalFiatFields,
  paymentsRoutesFields,
} from "./fields";

export enum Operations {
  GET_ACCOUNTS_BALANCES = "accounts_balances",
  GET_USER = "user",
  CALCULATE_PAYMENTS_LIMITS = "calculate_payments_limits",
  ESTIMATE_PAYMENT_FEE = "estimate_payment_fee",
  GET_PAYMENTS_LIMITS = "payments_limits",
  CREATE_WITHDRAWAL_FIAT = "create_withdrawal_fiat",
  GET_PAYMENTS_ROUTES = "payments_routes",
  CREATE_CONVERSION_QUOTE = "create_conversion_quote",
}

export const v4Operations = {
  getAccountsBalances: (variables: AccountsBalancesPayload) => {
    return gql.query({
      operation: Operations.GET_ACCOUNTS_BALANCES,
      variables: {
        user_id: { value: variables.user_id, required: false, type: "String" },
        currency_id: { value: variables.currency_id, required: false, type: "String" },
        hide_zero_balances: {
          value: variables.hide_zero_balances,
          required: false,
          type: "ToggleSwitch",
        },
        wallet_id: { value: variables.wallet_id, required: false, type: "String" },
      },
      fields: accountsBalancesFields,
    });
  },

  getUser: (variables: UserPayload) => {
    return gql.query({
      operation: Operations.GET_USER,
      variables: {
        user_id: { value: variables.user_id, required: false, type: "String" },
      },
      fields: userFields,
    });
  },

  getEstimatePaymentFee: (variables: EstimatePaymentFeePayload) => {
    return gql.mutation({
      operation: Operations.ESTIMATE_PAYMENT_FEE,
      variables: {
        payment_route_id: { value: variables.payment_route_id, required: true, type: "String" },
        type: { value: variables.type, required: true, type: "PaymentType" },
        amount: { value: variables.amount, required: true, type: "Float" },
      },
      fields: [],
    });
  },

  getPaymentsLimits: (variables: PaymentsLimitsPayload) => {
    return gql.query({
      operation: Operations.GET_PAYMENTS_LIMITS,
      variables: {
        currency_id: { value: variables.currency_id, required: true, type: "String" },
        limit_group_id: { value: variables.limit_group_id, required: true, type: "String" },
      },
      fields: paymentsLimitsFields,
    });
  },

  calculatePaymentsLimits: (variables: CalculatePaymentsLimitsPayload) => {
    return gql.mutation({
      operation: Operations.CALCULATE_PAYMENTS_LIMITS,
      variables: {
        currency_id: { value: variables.currency_id, required: true, type: "String" },
        type: { value: variables.type, required: true, type: "PaymentType" },
        notion_currency: { value: variables.notion_currency, required: true, type: "String" },
      },
      fields: calculatedPaymentsLimitsFields(variables.type),
    });
  },

  createWithdrawalFiat: (variables: CreateWithdrawalFiatPayload) => {
    return gql.mutation({
      operation: Operations.CREATE_WITHDRAWAL_FIAT,
      variables: {
        amount: { value: variables.amount, required: true, type: "Float" },
        payment_route_id: { value: variables.payment_route_id, required: true, type: "String" },
        currency_id: { value: variables.currency_id, required: true, type: "String" },
        mfa_token: { value: variables.mfa_token, required: false, type: "String" },
        wallet_id: { value: variables.wallet_id, required: false, type: "String" },
      },
      fields: createWithdrawalFiatFields,
    });
  },

  getPaymentsRoutes: (variables: PaymentsRoutesPayload) => {
    return gql.query({
      operation: Operations.GET_PAYMENTS_ROUTES,
      variables: {
        payment_route_id: { value: variables.payment_route_id, required: true, type: "String" },
      },
      fields: paymentsRoutesFields,
    });
  },

  createConversionQuote: (variables: CreateConversionQuotePayload) => {
    return gql.mutation({
      operation: Operations.CREATE_CONVERSION_QUOTE,
      variables: {
        source_currency_id: { value: variables.source_currency_id, required: true, type: "String" },
        target_currency_id: { value: variables.target_currency_id, required: true, type: "String" },
        source_currency_amount: { value: variables.source_currency_amount, required: false, type: "Float" },
        target_currency_amount: { value: variables.target_currency_amount, required: false, type: "Float" },
        fees_in_price: { value: variables.fees_in_price, required: false, type: "Boolean" },
        dry_run: { value: variables.dry_run, required: false, type: "ToggleSwitch" },
        ttl: { value: variables.ttl, required: false, type: "Int" },
      },
      fields: [
        "conversion_quote_id",
        "source_currency_id",
        "source_currency_amount",
        "target_currency_id",
        "target_currency_amount",
        "fee_currency_id",
        "fee_currency_amount",
        "fees_in_price",
        "price",
        "expires_at",
        { fees: ["currency_id", "amount"] },
      ],
    });
  },
};
